import omit from "lodash/omit";
import sortBy from "lodash/sortBy";

import {DashboardDefinition, DashboardDefinitionInput} from "@/lib/types/website.ts";

export const setDashboardPosition = (
  dashboardDef: DashboardDefinition,
  dashboardDefs: DashboardDefinition[]
): DashboardDefinition => {
  const dashboardPosition: number = dashboardDef.id
    ? sortBy(dashboardDefs, (d) => d.ordering).findIndex((dashboard) => dashboard.id === dashboardDef.id)
    : dashboardDefs.length;

  return {...dashboardDef, ...{dashboard_position: dashboardPosition}};
};

export const dashboardDefinitionToInput = (dashboardDef: DashboardDefinition): DashboardDefinitionInput => ({
  ...(omit(dashboardDef, ["created_at", "is_default_dashboard", "ordering"]) as DashboardDefinitionInput),
  // Saved placements come back with an id, the chart's name and a __typename, none of which the
  // input type accepts.
  charts: (dashboardDef.charts ?? []).map(({view_id, time_range}) => ({view_id, time_range})),
});

/**
 * Input for the dashboard stats query, without the charts. They are fetched by their own query,
 * and leaving them in would refetch every section whenever a chart was edited.
 */
export const dashboardDefinitionToStatsInput = (
  dashboardDef: DashboardDefinition
): Omit<DashboardDefinitionInput, "charts"> => omit(dashboardDefinitionToInput(dashboardDef), ["charts"]);

export const uptimePercentageCSSClass = (uptimePct: number, uptimeSla: number): string => {
  if (!uptimeSla) {
    return "";
  } else if (uptimePct >= (1.0 + uptimeSla) / 2.0) {
    return "text-success";
  } else if (uptimePct >= uptimeSla) {
    return "text-warning";
  } else {
    return "text-danger";
  }
};

export const responseTimeCSSClass = (responseTimeAvg: number, responseTimeSla: number | null): string => {
  if (!responseTimeSla) return "";

  if (responseTimeAvg <= responseTimeSla * 0.7) {
    return "text-success";
  } else if (responseTimeAvg <= responseTimeSla) {
    return "text-warning";
  } else {
    return "text-danger";
  }
};

export const rumLoadTimeCSSClass = (responseTimeAvg: number | null) => {
  if (!responseTimeAvg) {
    return "";
  }

  if (responseTimeAvg <= 5.5) {
    return "text-success";
  } else if (responseTimeAvg <= 9.0) {
    return "text-warning";
  } else {
    return "text-danger";
  }
};

export const uptimeGradeCSSClass = (grade: string) => {
  if (grade === "A") {
    return "text-success";
  } else if (grade === "B") {
    return "text-warning";
  } else {
    return "text-danger";
  }
};

export const expirationTimeCSSClass = (days: number, msp_threshold: number) => {
  if (days > msp_threshold) {
    return "text-success";
  } else if (days > 0) {
    return "text-warning";
  } else {
    return "text-danger";
  }
};
